import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

/** Weight class subdoc may use `Weight` (manager UI) or legacy `weight`. */
export function rankWeightValueFromWc(wc) {
  if (!wc || typeof wc !== "object") return undefined;
  const a = wc.Weight;
  if (a !== undefined && a !== null && String(a).trim() !== "") return a;
  const b = wc.weight;
  if (b !== undefined && b !== null && String(b).trim() !== "") return b;
  return undefined;
}

/** Match team rankings / lineup grouping (trim + lowercase). */
export function normalizeSchoolKeyForTeam(school) {
  const s = school == null ? "" : String(school).trim();
  if (!s) return "";
  return s.toLowerCase();
}

/**
 * All athletes at league + weight, Skill descending (client sort).
 * Uses only RankLeague + RankWeightClass equality (same as league summary full rosters);
 * avoids orderBy so behavior matches fetchSortedAthletesForLeagueWeight’s no-limit path.
 */
export async function fetchAllAthletesAtLeagueWeightOrderedBySkill(db, league, wc) {
  const weightVal = rankWeightValueFromWc(wc);
  if (!league || (weightVal !== 0 && !weightVal)) return [];
  const weightStr = String(weightVal);
  const weightNum = Number(weightVal);
  const tryUnordered = (wVal) => {
    const q = query(
      collection(db, "athletes"),
      where("RankLeague", "==", league),
      where("RankWeightClass", "==", wVal)
    );
    return getDocs(q).then((snap) => {
      const rows = [];
      snap.forEach((d) => rows.push({ id: d.id, data: d.data() }));
      rows.sort((a, b) => (b.data.Skill || 0) - (a.data.Skill || 0));
      return rows;
    });
  };
  let rows = await tryUnordered(weightVal).catch(() => []);
  if (
    rows.length === 0 &&
    weightNum !== weightVal &&
    !Number.isNaN(weightNum)
  ) {
    rows = await tryUnordered(weightNum).catch(() => []);
  }
  if (rows.length === 0 && weightStr !== weightVal) {
    rows = await tryUnordered(weightStr).catch(() => []);
  }
  return rows;
}

/**
 * Highest-Skill athlete for a school at one weight (composite index:
 * RankLeague, RankWeightClass, School, Skill desc).
 * @param {string} schoolExact must match Firestore School exactly
 */
export async function fetchTopAthleteSchoolAtWeight(
  db,
  league,
  wc,
  schoolExact
) {
  const trimmed = schoolExact == null ? "" : String(schoolExact).trim();
  const weightVal = rankWeightValueFromWc(wc);
  if (!league || !trimmed || (weightVal !== 0 && !weightVal)) return null;
  const weightStr = String(weightVal);
  const weightNum = Number(weightVal);
  const tryLimited = (wVal) => {
    const q = query(
      collection(db, "athletes"),
      where("RankLeague", "==", league),
      where("RankWeightClass", "==", wVal),
      where("School", "==", trimmed),
      orderBy("Skill", "desc"),
      limit(1)
    );
    return getDocs(q).then((snap) => {
      if (snap.empty) return null;
      const d = snap.docs[0];
      return { id: d.id, data: d.data() };
    });
  };
  let row = await tryLimited(weightVal).catch(() => null);
  if (!row && weightNum !== weightVal && !Number.isNaN(weightNum)) {
    row = await tryLimited(weightNum).catch(() => null);
  }
  if (!row && weightStr !== weightVal) {
    row = await tryLimited(weightStr).catch(() => null);
  }
  return row;
}

/**
 * Top athlete for school at weight: tries indexed exact School query first, then
 * full weight roster in Skill order and first row per normalized school (same as team rankings fallback).
 */
export async function fetchTopAthleteForSchoolAtWeight(db, league, wc, schoolHint) {
  const row = await fetchTopAthleteSchoolAtWeight(db, league, wc, schoolHint);
  if (row) return row;
  const key = normalizeSchoolKeyForTeam(schoolHint);
  if (!key) return null;
  const all = await fetchAllAthletesAtLeagueWeightOrderedBySkill(db, league, wc);
  for (const r of all) {
    const d = r.data || {};
    const sk =
      normalizeSchoolKeyForTeam(d.School) ||
      normalizeSchoolKeyForTeam(d.Team);
    if (sk === key) return r;
  }
  return null;
}
